/**
 * Admin — AI Missions Global Stats — FASE 7
 * /admin/ops/ai/missions
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Target, CheckCircle2, BarChart2, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/admin/ops/ai/missions")({
  component: AdminMissionsPage,
  head: () => ({ meta: [{ title: "Missions — AI — Admin — Structura" }] }),
});

interface MissionGlobalStats {
  total: number;
  byStatus: Array<{ status: string; count: number }>;
  byTemplate: Array<{ template_id: string; count: number; avgProgress: number }>;
  topTenants: Array<{ tenant_id: string; count: number }>;
  recent: Array<{
    tenant_id: string;
    title: string;
    status: string;
    progress: number;
    created_at: string;
  }>;
}

function KpiCard({ label, value, icon: Icon }: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-xl border bg-card p-4 flex items-start gap-3">
      <Icon className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
      <div>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function AdminMissionsPage() {
  const [stats, setStats] = useState<MissionGlobalStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void fetch("/api/admin/ai/missions", { credentials: "include" })
      .then((r) => r.json())
      .then((d: MissionGlobalStats) => setStats(d))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!stats) {
    return <div className="p-6 text-sm text-muted-foreground">Sin datos disponibles.</div>;
  }

  const completed = stats.byStatus.find((s) => s.status === "completed")?.count ?? 0;
  const active = stats.byStatus.find((s) => s.status === "active")?.count ?? 0;
  const completionRate = stats.total > 0 ? Math.round((completed / stats.total) * 100) : 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Target className="h-4 w-4 text-indigo-500" />
          Mission Orchestrator — Métricas globales
        </h2>
        <p className="text-sm text-muted-foreground">Todos los tenants · FASE 7 OS</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Total misiones" value={stats.total} icon={Target} />
        <KpiCard label="Activas" value={active} icon={BarChart2} />
        <KpiCard label="Completadas" value={completed} icon={CheckCircle2} />
        <KpiCard label="Tasa completitud" value={`${completionRate}%`} icon={Users} />
      </div>

      {stats.byTemplate.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Por plantilla</h3>
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Plantilla</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Count</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Progreso prom.</th>
                </tr>
              </thead>
              <tbody>
                {stats.byTemplate.map((t, i) => (
                  <tr key={t.template_id} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                    <td className="px-4 py-2 font-mono text-xs">{t.template_id}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{t.count}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{t.avgProgress}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats.topTenants.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Top tenants</h3>
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Tenant</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Misiones</th>
                </tr>
              </thead>
              <tbody>
                {stats.topTenants.map((t, i) => (
                  <tr key={t.tenant_id} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                    <td className="px-4 py-2 font-mono text-xs truncate max-w-[200px]">{t.tenant_id}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{t.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats.recent.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Recientes</h3>
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Título</th>
                  <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Tenant</th>
                  <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Estado</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Progreso</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map((m, i) => (
                  <tr key={`${m.tenant_id}-${m.created_at}-${i}`} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                    <td className="px-4 py-2 text-xs max-w-[180px] truncate">{m.title}</td>
                    <td className="px-4 py-2 font-mono text-[10px] text-muted-foreground truncate max-w-[120px]">
                      {m.tenant_id}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">{m.progress}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
